import React, { useMemo } from 'react';
import moment from 'moment';
import MagicForm from '../../../../../../Components/Form/MagicForm';
import { ItemScheme } from '../../../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../../../Enums/MagicField.enum';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import useLoading from '../../../../../Base/Hooks/useLoading';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import {
	CreateInvoiceInsuranceCompanyBatchMutation,
	useCreateInvoiceInsuranceCompanyBatchMutation,
	useTermItemsQuery,
	useUserEmailQuery,
	useWorkplaceBasicDataQuery,
} from '../../../../../../graphql/graphql';
import { TermType } from '../../../../../Term/Enums/TermType.enum';
import { SelectValue } from '../../../../../../Components/Base/Select/Select';
import { getDefaultData } from '../../../../../MagicForm/Services/magicForm-defaultsData.service';
import { useManagerContext } from '../../../../Services/ManagerContext';
import { useAppContext } from '../../../../../../Services/AppContext';
import Loading from '../../../../../../Components/Loading';

type InvoiceBatch =
	CreateInvoiceInsuranceCompanyBatchMutation['createInvoiceInsuranceCompanyBatch'];

type FeedbackItemFormValue = {
	feedbackType: SelectValue;
	messageType: SelectValue;
};

interface Props {
	batchId: string;
	onSuccess: (invoiceBatch: InvoiceBatch) => void;
}

interface FormValues {
	invoiceDate: Date | string;
	note?: string;
	enableFeedback?: boolean;
	keepSettings?: SelectValue;
	feedbackItems?: FeedbackItemFormValue[];
	email?: string;
	sms?: string;
}

const FEEDBACK_CHANNEL_SMS = 'F';
const FEEDBACK_CHANNEL_EMAIL = 'M';

const FEEDBACK_INVALID_MESSAGE =
	'component.insuranceCompanyBatch.invoiceForm.feedbackInvalid';

const getTermItemIdsByChannel = (
	feedbackTypeById: Map<string, string>,
	channelValue: string,
) =>
	[...feedbackTypeById.entries()]
		.filter(([, value]) => value === channelValue)
		.map(([id]) => id);

const createFormScheme = (
	smsTermItemIds: string[],
	emailTermItemIds: string[],
): ItemScheme[] => [
	{
		type: MagicField.DATEPICKER,
		key: 'invoiceDate',
		title: 'component.insuranceCompanyBatch.invoiceForm.invoiceDate',
		settings: { required: true, portal: true },
	},
	{
		type: MagicField.TEXTAREA,
		key: 'note',
		title: 'component.insuranceCompanyBatch.invoiceForm.note',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'enableFeedback',
		title: 'component.insuranceCompanyBatch.invoiceForm.enableFeedback',
	},
	{
		type: MagicField.CONDITION,
		settings: {
			rules: [{ formControlKey: 'enableFeedback', rule: 'equal', value: true }],
		},
		children: [
			{
				type: MagicField.TERM_ITEM_SELECT,
				key: 'keepSettings',
				title:
					'component.insuranceCompanyBatch.invoiceForm.feedbackKeepSettings',
				settings: {
					required: true,
					portal: true,
					termType: TermType.VZP_INVOICE_FEEDBACK_KEEP_SETTINGS,
					labelFormat: '{value} - {name}',
				},
			},
			{
				type: MagicField.MULTIPLIER,
				key: 'feedbackItems',
				settings: {
					createDefault: 1,
					min: 1,
					addButtonTitle:
						'component.insuranceCompanyBatch.invoiceForm.feedbackAddItem',
				},
				children: [
					{
						type: MagicField.HSTACK,
						children: [
							{
								type: MagicField.TERM_ITEM_SELECT,
								key: 'messageType',
								title:
									'component.insuranceCompanyBatch.invoiceForm.feedbackMessageType',
								settings: {
									required: true,
									portal: true,
									termType: TermType.VZP_INVOICE_FEEDBACK_MESSAGE_TYPE,
									labelFormat: '{value} - {name}',
								},
							},
							{
								type: MagicField.TERM_ITEM_SELECT,
								key: 'feedbackType',
								title:
									'component.insuranceCompanyBatch.invoiceForm.feedbackChannel',
								settings: {
									required: true,
									portal: true,
									termType: TermType.VZP_INVOICE_FEEDBACK_TYPE,
									labelFormat: '{value} - {name}',
								},
							},
						],
					},
					{ type: MagicField.MULTIPLIER_REMOVE_BUTTON },
				],
			},
			{
				type: MagicField.EMAIL,
				key: 'email',
				title: 'component.insuranceCompanyBatch.invoiceForm.feedbackEmail',
				settings: {
					requiredWhenAnyInArray: {
						arrayKey: 'feedbackItems',
						field: 'feedbackType',
						matchingValues: emailTermItemIds,
					},
					errorMessage: FEEDBACK_INVALID_MESSAGE,
				},
			},
			{
				type: MagicField.PHONE_NUMBER,
				key: 'sms',
				title: 'component.insuranceCompanyBatch.invoiceForm.feedbackSms',
				settings: {
					requiredWhenAnyInArray: {
						arrayKey: 'feedbackItems',
						field: 'feedbackType',
						matchingValues: smsTermItemIds,
					},
					errorMessage: FEEDBACK_INVALID_MESSAGE,
				},
			},
		],
	},
];

const ManagerInsuranceCompaniesBatchInvoiceForm = ({
	batchId,
	onSuccess,
}: Props) => {
	const { workplaceId } = useManagerContext();
	const { loggedUserId } = useAppContext();
	const { isLoading, setIsLoading } = useLoading();
	const { displayGQLError, showUnexpectedError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const [createInvoice] = useCreateInvoiceInsuranceCompanyBatchMutation();
	const { data: workplaceData, isLoading: isLoadingWorkplace } =
		useWorkplaceBasicDataQuery({ id: workplaceId });
	const { data: userEmailData, isLoading: isLoadingUserEmail } =
		useUserEmailQuery({ id: loggedUserId });
	const { data: feedbackTypeTerms, isLoading: isLoadingFeedbackTypes } =
		useTermItemsQuery({
			termType: TermType.VZP_INVOICE_FEEDBACK_TYPE,
			pagination: { take: 999 },
		});

	const feedbackTypeById = useMemo(() => {
		const map = new Map<string, string>();

		for (const item of feedbackTypeTerms?.termItems?.data ?? []) {
			if (item?.id && item.value) {
				map.set(item.id, item.value);
			}
		}

		return map;
	}, [feedbackTypeTerms]);

	const formScheme = useMemo(
		() =>
			createFormScheme(
				getTermItemIdsByChannel(feedbackTypeById, FEEDBACK_CHANNEL_SMS),
				getTermItemIdsByChannel(feedbackTypeById, FEEDBACK_CHANNEL_EMAIL),
			),
		[feedbackTypeById],
	);

	const defaultValues = useMemo(() => {
		if (!workplaceData?.workplace) {
			return undefined;
		}

		return {
			...getDefaultData({ data: formScheme }),
			invoiceDate: moment().toDate(),
			enableFeedback: false,
			sms: workplaceData.workplace.phone || undefined,
			email: userEmailData?.user?.email || undefined,
		};
	}, [workplaceData, userEmailData, formScheme]);

	const onSubmit = async (values: FormValues) => {
		setIsLoading(true);

		try {
			const response = await createInvoice({
				input: {
					batchId,
					invoiceDate: moment(values.invoiceDate).toISOString(),
					note: values.note || undefined,
					feedback: values.enableFeedback
						? {
								keepSettingsTermItemId: values.keepSettings!.value as string,
								email: values.email?.trim() || undefined,
								sms: values.sms?.trim() || undefined,
								items: (values.feedbackItems ?? []).map(item => ({
									feedbackTypeTermItemId: item.feedbackType.value as string,
									messageTypeTermItemId: item.messageType.value as string,
								})),
						  }
						: undefined,
				},
			});

			if ('data' in response && response.data) {
				showNotificationSuccess({
					content: 'component.insuranceCompanyBatch.invoiceForm.successMessage',
				});
				onSuccess(response.data.createInvoiceInsuranceCompanyBatch);
			}

			displayGQLError(response);
		} catch {
			showUnexpectedError();
		} finally {
			setIsLoading(false);
		}
	};

	if (
		isLoadingWorkplace ||
		isLoadingUserEmail ||
		isLoadingFeedbackTypes ||
		!defaultValues
	) {
		return <Loading />;
	}

	return (
		<MagicForm
			formScheme={formScheme}
			defaultValues={defaultValues}
			onSubmit={onSubmit}
			disableSubmit={isLoading}
		/>
	);
};

export default ManagerInsuranceCompaniesBatchInvoiceForm;
